import React from "react";
import type { BlockType, CardRow, CardMeta } from "../../../data/types";
import { getRowBlockType, getBlockVariant, LAYOUT_BLOCK_TYPES } from "../../../data/types";
import LeafBlockRenderer from "./LeafBlockRenderer";
import * as LucideIcons from "lucide-react";

function LucideIcon({ name, className }: { name: string; className?: string }) {
  // Convert kebab-case to PascalCase: "git-branch" → "GitBranch"
  const pascal = name.replace(/(^|-)([a-z])/g, (_: string, __: string, c: string) => c.toUpperCase());
  const Icon = (LucideIcons as any)[pascal];
  if (!Icon) return null;
  return <Icon className={className} />;
}

export interface LayoutBlockRendererProps {
  blockType: BlockType;
  content: any;
  variant: string;
  readOnly: boolean;
  onUpdate: (c: any) => void;
  resolveAsset?: (s: string) => string;
  dark?: boolean;
  cardMeta?: CardMeta;
}

/* ── Helpers ─────────────────────────────────────────────────── */

function gi(c: any): any[] {
  return !c ? [] : Array.isArray(c) ? c : Array.isArray(c?.items) ? c.items : [];
}

function grc(c: any, n: number): string {
  const e = typeof c?.cols === "number" && c.cols >= 1 ? c.cols : 0;
  if (e === 1) return "grid-cols-1";
  if (e === 2) return "grid-cols-1 sm:grid-cols-2";
  if (e === 3) return "grid-cols-1 sm:grid-cols-3";
  if (e >= 4) return "grid-cols-2 sm:grid-cols-" + e;
  if (n <= 1) return "grid-cols-1";
  if (n === 2) return "grid-cols-1 sm:grid-cols-2";
  if (n === 3) return "grid-cols-1 sm:grid-cols-3";
  return "grid-cols-2";
}

function tx(i: any): string {
  return typeof i === "string" ? i : i?.text || i?.title || "";
}
function tl(i: any): string {
  return i?.title || i?.label || i?.name || "";
}
function ds(i: any): string {
  return i?.description || i?.desc || (i?.title && i?.text ? i.text : "");
}
function im(i: any, r?: (s: string) => string): string | null {
  let s: string | null = null;
  if (i?.image) s = typeof i.image === "string" ? i.image : i.image?.src ?? null;
  else if (i?.src) s = i.src;
  else if (i?.icon && typeof i.icon === "string") s = i.icon;
  return s && r ? r(s) : s;
}
function vl(i: any): string {
  return i?.value != null ? String(i.value) : i?.number != null ? String(i.number) : tx(i);
}

export const _A = [
  { bg: "from-indigo-500/10 to-indigo-500/5", bd: "border-indigo-200/40", t: "text-indigo-600", d: "bg-indigo-500" },
  { bg: "from-violet-500/10 to-violet-500/5", bd: "border-violet-200/40", t: "text-violet-600", d: "bg-violet-500" },
  { bg: "from-sky-500/10 to-sky-500/5", bd: "border-sky-200/40", t: "text-sky-600", d: "bg-sky-500" },
  { bg: "from-emerald-500/10 to-emerald-500/5", bd: "border-emerald-200/40", t: "text-emerald-600", d: "bg-emerald-500" },
];
export const _DA = [
  { bg: "from-indigo-400/15 to-indigo-400/5", bd: "border-indigo-400/20", t: "text-indigo-300", d: "bg-indigo-400" },
  { bg: "from-violet-400/15 to-violet-400/5", bd: "border-violet-400/20", t: "text-violet-300", d: "bg-violet-400" },
  { bg: "from-sky-400/15 to-sky-400/5", bd: "border-sky-400/20", t: "text-sky-300", d: "bg-sky-400" },
  { bg: "from-emerald-400/15 to-emerald-400/5", bd: "border-emerald-400/20", t: "text-emerald-300", d: "bg-emerald-400" },
];

/* ── Features — card grid with inline icons ──────────────────── */

function Features({ content, resolveAsset, dark }: {
  content: any; variant: string; resolveAsset?: (s: string) => string; dark?: boolean;
}) {
  const its = gi(content);
  const g = grc(content, its.length);
  const tb = dark ? "text-white/90" : "text-slate-800";
  const ts = dark ? "text-white/60" : "text-slate-500";
  const flat = content?.flat === true;

  return (
    <div className={`grid ${g} ${flat ? "gap-2" : "gap-4"}`}>
      {its.map((item: any, i: number) => {
        const title = tl(item);
        const desc = ds(item);
        const iconName = item?.icon || null;
        const iconSrc = iconName && resolveAsset ? resolveAsset(iconName) : null;
        const isLucide = iconName && !iconName.includes("/") && !iconName.includes(".");
        const wrapCls = flat
          ? "py-2"
          : `p-5 rounded-xl ${dark ? "bg-white/[0.06] ring-1 ring-white/10" : "bg-white ring-1 ring-slate-200/60 shadow-sm"}`;
        return (
          <div key={i} className={wrapCls}>
            <div className="flex items-center gap-4">
              {isLucide && (
                <div className={`${flat ? "size-8 rounded-lg" : "size-10 rounded-xl"} flex items-center justify-center shrink-0 ${dark ? "bg-white/10" : "bg-indigo-50"}`}>
                  <LucideIcon name={iconName} className={`${flat ? "size-4" : "size-5"} ${dark ? "text-indigo-300" : "text-indigo-500"}`} />
                </div>
              )}
              {iconSrc && !isLucide && <img src={iconSrc} alt="" className="size-9 object-contain shrink-0" loading="lazy" />}
              <div className="min-w-0">
                {title && <h4 className={`text-[0.95rem] font-semibold ${tb}`}>{title}</h4>}
                {desc && <p className={`text-[0.85rem] leading-relaxed ${ts} mt-0.5`}>{desc}</p>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Stats ────────────────────────────────────────────────────── */

function Stats({ content, dark }: { content: any; variant: string; dark?: boolean }) {
  const its = gi(content);
  const g = grc(content, its.length);
  const tb = dark ? "text-white/90" : "text-slate-800";
  const ts = dark ? "text-white/60" : "text-slate-500";
  return (
    <div className={`grid ${g} gap-4 py-4`}>
      {its.map((item: any, i: number) => {
        const v = vl(item), title = tl(item);
        return (
          <div key={i} className={`p-5 rounded-xl text-center ${dark ? "bg-white/[0.06]" : "bg-slate-50"}`}>
            <div className={`text-3xl font-bold ${tb}`}>{v}</div>
            {title && <div className={`mt-1.5 text-[0.85rem] ${ts}`}>{title}</div>}
          </div>
        );
      })}
    </div>
  );
}

/* ── Timeline ─────────────────────────────────────────────────── */

function Timeline({ content, dark }: { content: any; variant: string; dark?: boolean }) {
  const its = gi(content);
  const tb = dark ? "text-white/90" : "text-slate-800";
  const ts = dark ? "text-white/65" : "text-slate-600";
  const dc = dark ? "bg-indigo-400" : "bg-indigo-500";
  const lc = dark ? "bg-indigo-400/30" : "bg-indigo-200";
  return (
    <div className="py-5 relative">
      <div className={`absolute left-[7px] top-0 bottom-0 w-0.5 ${lc}`} />
      <div className="space-y-6">
        {its.map((item: any, i: number) => (
          <div key={i} className="relative pl-8 flex items-start">
            <div className={`absolute left-0 top-1 size-4 rounded-full ${dc} ring-4 ${dark ? "ring-slate-900" : "ring-white"} shadow-sm`} />
            <div>
              {tl(item) && <div className={`text-[0.95rem] font-semibold mb-0.5 ${tb}`}>{tl(item)}</div>}
              <p className={`text-[0.9rem] leading-relaxed ${ts}`}>{tx(item)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Gallery ──────────────────────────────────────────────────── */

function Gallery({ content, resolveAsset, dark: _dark }: {
  content: any; variant: string; resolveAsset?: (s: string) => string; dark?: boolean;
}) {
  const its = gi(content);
  const g = grc(content, its.length);
  return (
    <div className={`grid ${g} gap-4 py-4`}>
      {its.map((item: any, i: number) => {
        const src = im(item, resolveAsset);
        return (
          <div key={i} className="rounded-xl overflow-hidden shadow-md ring-1 ring-black/5">
            {src
              ? <img src={src} alt={tx(item)} className="w-full h-40 object-cover" loading="lazy" />
              : <div className="h-40 flex items-center justify-center text-sm bg-slate-100 text-slate-400">{tx(item) || "Image"}</div>}
          </div>
        );
      })}
    </div>
  );
}

/* ── Steps ────────────────────────────────────────────────────── */

function Steps({ content, dark }: { content: any; variant: string; dark?: boolean }) {
  const its = gi(content);
  const tb = dark ? "text-white/90" : "text-slate-800";
  const ts = dark ? "text-white/65" : "text-slate-600";
  return (
    <div className="py-4 space-y-4">
      {its.map((item: any, i: number) => (
        <div key={i} className="flex items-start gap-4">
          <div className={`size-8 rounded-lg flex items-center justify-center shrink-0 ${dark ? "bg-indigo-500/20 text-indigo-300" : "bg-indigo-50 text-indigo-600"} text-sm font-bold`}>{i + 1}</div>
          <div className="pt-0.5">
            {tl(item) && <h4 className={`text-[0.95rem] font-semibold mb-0.5 ${tb}`}>{tl(item)}</h4>}
            <p className={`text-[0.9rem] leading-relaxed ${ts}`}>{tx(item)}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Comparison ───────────────────────────────────────────────── */

function Comparison({ content, dark }: { content: any; variant: string; dark?: boolean }) {
  const its = gi(content);
  const ts = dark ? "text-white/65" : "text-slate-600";
  return (
    <div className="grid grid-cols-2 gap-5 py-4">
      {its.map((item: any, i: number) => (
        <div key={i} className={`p-5 rounded-xl ${dark ? "bg-white/[0.06] ring-1 ring-white/10" : "bg-white ring-1 ring-slate-200/60 shadow-sm"}`}>
          <p className={`text-[0.9rem] leading-relaxed ${ts}`}>{tx(item)}</p>
        </div>
      ))}
    </div>
  );
}

/* ── Pricing ──────────────────────────────────────────────────── */

function Pricing({ content, dark }: { content: any; variant: string; dark?: boolean }) {
  const its = gi(content);
  const tb = dark ? "text-white/90" : "text-slate-800";
  const g = grc(content, its.length);
  return (
    <div className={`grid ${g} gap-5 py-4`}>
      {its.map((item: any, i: number) => (
        <div key={i} className={`p-6 rounded-xl text-center ${dark ? "bg-white/[0.06]" : "bg-slate-50"}`}>
          {tl(item) && <div className={`text-sm font-semibold mb-2 ${tb}`}>{tl(item)}</div>}
          <div className="text-3xl font-extrabold text-indigo-600 mb-2">{vl(item)}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Logos ─────────────────────────────────────────────────────── */

function Logos({ content, resolveAsset, dark }: {
  content: any; variant: string; resolveAsset?: (s: string) => string; dark?: boolean;
}) {
  const its = gi(content);
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 py-6">
      {its.map((item: any, i: number) => (
        <div key={i} className={`flex items-center justify-center p-4 rounded-xl ${dark ? "bg-white/5" : "bg-slate-50/80"}`}>
          {im(item, resolveAsset)
            ? <img src={im(item, resolveAsset)!} alt={tx(item)} className="h-10 object-contain" loading="lazy" />
            : <span className="text-sm text-slate-400">{tx(item) || "Logo"}</span>}
        </div>
      ))}
    </div>
  );
}

/* ── Testimonial ──────────────────────────────────────────────── */

function Testimonial({ content, dark }: { content: any; variant: string; dark?: boolean }) {
  const its = gi(content);
  const ts = dark ? "text-white/65" : "text-slate-600";
  return (
    <div className="space-y-4 py-4">
      {its.map((item: any, i: number) => (
        <div key={i} className="py-2">
          <p className={`text-[0.95rem] leading-relaxed italic ${ts}`}>{tx(item)}</p>
        </div>
      ))}
    </div>
  );
}

/* ── Dispatch ─────────────────────────────────────────────────── */

function dispatchLayout(p: LayoutBlockRendererProps): React.JSX.Element {
  switch (p.blockType) {
    case "columns": return <Cols content={p.content} variant={p.variant} resolveAsset={p.resolveAsset} dark={p.dark} cardMeta={p.cardMeta} readOnly={p.readOnly} />;
    case "features": return <Features content={p.content} variant={p.variant} resolveAsset={p.resolveAsset} dark={p.dark} />;
    case "stats": return <Stats content={p.content} variant={p.variant} dark={p.dark} />;
    case "timeline": return <Timeline content={p.content} variant={p.variant} dark={p.dark} />;
    case "gallery": return <Gallery content={p.content} variant={p.variant} resolveAsset={p.resolveAsset} dark={p.dark} />;
    case "steps": return <Steps content={p.content} variant={p.variant} dark={p.dark} />;
    case "comparison": return <Comparison content={p.content} variant={p.variant} dark={p.dark} />;
    case "pricing": return <Pricing content={p.content} variant={p.variant} dark={p.dark} />;
    case "logos": return <Logos content={p.content} variant={p.variant} resolveAsset={p.resolveAsset} dark={p.dark} />;
    case "testimonial": return <Testimonial content={p.content} variant={p.variant} dark={p.dark} />;
    default: return <div className="py-3 px-4 rounded-xl text-sm bg-slate-50 text-slate-400">{String(p.blockType)}</div>;
  }
}

/* ── Nested block renderer ────────────────────────────────────── */

function RB({ row, cardMeta, readOnly, resolveAsset, dark }: {
  row: CardRow; cardMeta?: CardMeta; readOnly: boolean; resolveAsset?: (s: string) => string; dark?: boolean;
}) {
  const bt = getRowBlockType(row);
  if (!bt) return null;
  if (LAYOUT_BLOCK_TYPES.includes(bt as any)) {
    const v = getBlockVariant(bt, cardMeta ?? {});
    return dispatchLayout({ blockType: bt, content: row[bt], variant: v, readOnly, onUpdate: () => {}, resolveAsset, dark, cardMeta });
  }
  return <LeafBlockRenderer blockType={bt} content={row[bt]} readOnly={readOnly} onUpdate={() => {}} resolveAsset={resolveAsset} dark={dark} />;
}

function NB({ blocks, cardMeta, readOnly, resolveAsset, dark }: {
  blocks: CardRow[]; cardMeta?: CardMeta; readOnly: boolean; resolveAsset?: (s: string) => string; dark?: boolean;
}) {
  return (
    <div className="flex flex-col h-full justify-center">
      {blocks.map((r, i) => <RB key={i} row={r} cardMeta={cardMeta} readOnly={readOnly} resolveAsset={resolveAsset} dark={dark} />)}
    </div>
  );
}

/* ── Columns — nested blocks, split ratios, image self-center ── */

function Cols({ content, variant, resolveAsset, dark, cardMeta, readOnly }: {
  content: any; variant: string; resolveAsset?: (s: string) => string; dark?: boolean; cardMeta?: CardMeta; readOnly: boolean;
}) {
  const its = gi(content);
  const ts = dark ? "text-white/65" : "text-slate-600";
  const tb = dark ? "text-white/90" : "text-slate-800";
  const hasN = its.some((i: any) => Array.isArray(i?.blocks));

  if (hasN) {
    const sp = typeof content?.split === "string"
      ? content.split.split(":").map(Number).filter((n: number) => n > 0)
      : [];
    const tpl = sp.length === its.length
      ? sp.map((p: number) => p + "fr").join(" ")
      : its.map(() => "1fr").join(" ");

    return (
      <div className="grid gap-6 py-4" style={{ gridTemplateColumns: tpl, alignItems: "center" }}>
        {its.map((item: any, i: number) => {
          if (Array.isArray(item?.blocks)) {
            const isCard = item?.card === true;
            const cardCls = isCard
              ? (dark ? "p-6 rounded-2xl bg-white/[0.06] ring-1 ring-white/10" : "p-6 rounded-2xl bg-white ring-1 ring-slate-200/60 shadow-sm")
              : "";
            return (
              <div key={i} className={`flex flex-col justify-center ${cardCls}`}>
                <NB blocks={item.blocks} cardMeta={cardMeta} readOnly={readOnly} resolveAsset={resolveAsset} dark={dark} />
              </div>
            );
          }
          const src = im(item, resolveAsset);
          if (src) {
            return (
              <div key={i} className="self-center rounded-2xl overflow-hidden shadow-lg ring-1 ring-black/5">
                <img src={src} alt="" className="w-full" loading="lazy" />
              </div>
            );
          }
          return (
            <div key={i} className="flex flex-col justify-center">
              <p className={`text-[0.95rem] leading-relaxed ${ts}`}>{tx(item)}</p>
            </div>
          );
        })}
      </div>
    );
  }
  const g = grc(content, its.length);
  return (
    <div className={`grid ${g} gap-5 py-4`}>
      {its.map((item: any, i: number) => {
        const t = tx(item), title = tl(item), d = ds(item), src = im(item, resolveAsset);
        if (variant === "cards") {
          return (
            <div key={i} className={`rounded-2xl overflow-hidden ${dark ? "bg-white/8 ring-1 ring-white/10" : "bg-white ring-1 ring-black/5 shadow-md"} hover:shadow-lg transition-all`}>
              {src && <img src={src} alt="" className="w-full h-40 object-cover" loading="lazy" />}
              <div className="p-5">
                {title && <h4 className={`text-[1rem] font-semibold mb-1.5 ${tb}`}>{title}</h4>}
                <p className={`text-[0.9rem] leading-relaxed ${ts}`}>{d || t}</p>
              </div>
            </div>
          );
        }
        return (
          <div key={i} className="p-1">
            {src && <div className="rounded-xl overflow-hidden mb-3"><img src={src} alt="" className="w-full rounded-xl" loading="lazy" /></div>}
            <p className={`text-[0.95rem] leading-relaxed ${ts}`}>{t}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function LayoutBlockRenderer(props: LayoutBlockRendererProps) {
  return dispatchLayout(props);
}
